import { useState } from "react";
import { TextField, Button } from "@material-ui/core";
import copy from "../../../../assets/icons/noun_copy_3992683.png";
import cancel from "../../../../assets/icons/noun_X_2172405.png";
import edit from "../../../../assets/icons/noun_Pen_3998318.png";
import check from "../../../../assets/icons/noun_Check Box_38652.png";
import { ProviderContext, withSnackbar } from "notistack";
import FirebaseService from "../../../../services/firebase/FirebaseService";
import "./ClassCodeView.css";
import { GENERAL_MAX_LENGTH } from "../../../../utils/stringFormatting";
import WordDivider from "../../../WordDivider";
import HelpLink from "../../../HelpLink";

interface ViewProps {
  classCode: string;
  classId: string;
  editable?: boolean;
}

interface ViewState {
  editableValue: string;
  error: Array<string>;
  isSaving: boolean;
  editing: boolean;
}

type EditableCopyTextInputProps = ViewProps &
  ProviderContext & {
    value: string;
  };

const EditableCopyTextInput = (props: EditableCopyTextInputProps) => {
  const [state, setState] = useState<ViewState>({
    editing: false,
    editableValue: props.value,
    error: [],
    isSaving: false,
  });

  const { editing, editableValue, error, isSaving } = state;
  const { value, editable } = props;

  return editing ? (
    <div style={{ paddingBottom: "3px" }}>
      <div className="center-contents">
        <div className="input-layout">
          <TextField
            value={editableValue}
            onChange={e => setState({ ...state, editableValue: e.target.value })}
            className="input-style"
            inputProps={{ maxLength: GENERAL_MAX_LENGTH }}
          />
          <Button
            style={{ padding: "0" }}
            onClick={() => {
              if (!isSaving) {
                setState({ ...state, isSaving: true });

                // Exit if class code has not changed at all
                if (props.value === editableValue) {
                  setState({ ...state, editing: false, isSaving: false });
                  return;
                }

                // check for class code formatting errors
                const tempError: Array<string> = [];
                if (editableValue.length < 6) {
                  tempError.push("*Class codes must have at least 6-characters\n");
                }
                const valid = new RegExp(/[\s !@#*$%]/);
                if (valid.test(editableValue)) {
                  tempError.push("*Cannot contain characters !, @, #, $, %, *, or blank spaces");
                }

                // Return if errors exist
                if (tempError.length) {
                  setState({ ...state, error: tempError, isSaving: false });
                  return;
                }

                // Update class code in backend and return
                FirebaseService.updateClassCode(props.classCode, editableValue, props.classId)
                  .then(res => {
                    props.enqueueSnackbar(res, { variant: "success", autoHideDuration: 5000 });
                    setState({ ...state, editing: false, isSaving: false, error: [] });
                  })
                  .catch(e => {
                    const message = e instanceof Error ? e.message : "Something went wrong, please try again!";
                    setState({ ...state, error: [message], isSaving: false });
                  });
              }
            }}
          >
            <img height={45} src={check} alt="save" />
          </Button>
        </div>
        <Button onClick={() => setState({ ...state, editing: false, error: [], editableValue: value })}>
          <img height={25} color="#FC6954" src={cancel} alt="cancel" />
        </Button>
      </div>
      {error.map((e: string) => (
        <p key={e} className="error">
          {e}
        </p>
      ))}
    </div>
  ) : (
    <div>
      <div className="center-contents">
        <div className="row" style={{ backgroundColor: "#FBF9F7" }}>
          <code className="display-code">{value}</code>
          <div className="action-buttons">
            <button
              onClick={() => {
                navigator.clipboard
                  .writeText(value)
                  .then(() => {
                    props.enqueueSnackbar("Class Code Copied!", {
                      variant: "success",
                      autoHideDuration: 5000,
                    });
                  })
                  .catch(() => {
                    props.enqueueSnackbar("Failed to copy Class Code.", {
                      variant: "error",
                      autoHideDuration: 5000,
                    });
                  });
              }}
            >
              <img height={30} color="#FC6954" src={copy} alt="copy" />
            </button>
            {editable && (
              <button onClick={() => setState({ ...state, editing: true })}>
                <img height={30} src={edit} alt="edit" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ClassCodeView = (props: ViewProps & ProviderContext) => {
  return (
    <div className="classCodeView">
      <div className="title">Share this class link with students</div>
      <EditableCopyTextInput {...props} value={`${window.origin}/j/${props.classCode}`} />
      <WordDivider>OR</WordDivider>
      <div className="title">Have students enter your unique Class Code</div>
      <EditableCopyTextInput {...props} value={props.classCode} editable />
      <div className="subtext">
        Detailed instructions available in our{" "}
        <HelpLink type="article" articleId="1500006404642-Adding-Students-to-Readlee" className="help-link">
          Help Center
        </HelpLink>
        .
      </div>
    </div>
  );
};

export default withSnackbar(ClassCodeView);
